import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useForgotPasswordMutation } from "../lib/api/auth.js";
import { APIError } from "../lib/http.js";

const schema = z.object({ email: z.string().email("Enter a valid email").max(255) });
type Values = z.infer<typeof schema>;

export function ForgotPasswordPage(): JSX.Element {
  const mut = useForgotPasswordMutation();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const submitError = mut.error
    ? mut.error instanceof APIError
      ? mut.error.displayDetail
      : (mut.error as Error).message
    : null;

  return (
    <main className="flex h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Forgot password</CardTitle>
          <CardDescription>We'll send a reset link if your email is registered.</CardDescription>
        </CardHeader>
        <CardContent>
          {mut.isSuccess ? (
            <div className="space-y-4">
              <p className="text-sm">
                If your email is registered, we sent a reset link. Check your inbox.
              </p>
              {mut.data.devLink ? (
                <p className="rounded-md border border-dashed border-border bg-card p-3 text-xs">
                  <span className="font-medium">Dev:</span>{" "}
                  <a href={mut.data.devLink} className="break-all text-primary hover:underline">
                    {mut.data.devLink}
                  </a>
                </p>
              ) : null}
              <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">
                Back to sign in
              </Link>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit((v) => mut.mutate(v))} className="space-y-3" noValidate>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl><Input type="email" autoComplete="email" autoFocus {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
                <Button type="submit" className="w-full" disabled={mut.isPending}>
                  {mut.isPending ? "Sending…" : "Send reset link"}
                </Button>
                <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">
                  Back to sign in
                </Link>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
